'use client';
import React from 'react';
import { Activity, Zap, AlertTriangle, Shield, Database, Layers } from 'lucide-react';
import type { SimMetrics, SimulationState } from './ArchitectureCanvasScreen';

interface LiveMetricsBarProps {
  metrics: SimMetrics;
  simState: SimulationState;
  isLandscape?: boolean;
}

interface MetricConfig {
  key: keyof SimMetrics;
  label: string;
  unit: string;
  icon: React.ElementType;
  format: (v: number) => string;
  thresholds: { warn: number; crit: number; invert?: boolean };
}

const METRIC_CONFIGS: MetricConfig[] = [
  {
    key: 'rps',
    label: 'RPS',
    unit: 'req/s',
    icon: Zap,
    format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`,
    thresholds: { warn: 0, crit: 0 },
  },
  {
    key: 'p99',
    label: 'P99',
    unit: 'ms',
    icon: Activity,
    format: (v) => `${v.toFixed(1)}`,
    thresholds: { warn: 100, crit: 300 },
  },
  {
    key: 'errorRate',
    label: 'Errors',
    unit: '%',
    icon: AlertTriangle,
    format: (v) => `${v.toFixed(2)}`,
    thresholds: { warn: 1, crit: 5 },
  },
  {
    key: 'availability',
    label: 'Avail',
    unit: '%',
    icon: Shield,
    format: (v) => `${v.toFixed(2)}`,
    thresholds: { warn: 99.9, crit: 99.0, invert: true },
  },
  {
    key: 'cacheHit',
    label: 'Cache',
    unit: '%',
    icon: Layers,
    format: (v) => `${v.toFixed(1)}`,
    thresholds: { warn: 50, crit: 20, invert: true },
  },
  {
    key: 'dbLoad',
    label: 'DB',
    unit: '%',
    icon: Database,
    format: (v) => `${v.toFixed(1)}`,
    thresholds: { warn: 70, crit: 90 },
  },
];

function getMetricStatus(value: number, thresholds: MetricConfig['thresholds'], simActive: boolean): 'idle' | 'healthy' | 'warn' | 'crit' {
  if (!simActive) return 'idle';
  if (thresholds.warn === 0 && thresholds.crit === 0) return 'healthy';
  if (thresholds.invert) {
    if (value < thresholds.crit) return 'crit';
    if (value < thresholds.warn) return 'warn';
    return 'healthy';
  }
  if (value >= thresholds.crit) return 'crit';
  if (value >= thresholds.warn) return 'warn';
  return 'healthy';
}

export default function LiveMetricsBar({ metrics, simState, isLandscape = false }: LiveMetricsBarProps) {
  const isActive = simState !== 'stopped';

  // Landscape: ultra-compact HUD metrics strip
  if (isLandscape) {
    return (
      <div className="border-t border-border/60 bg-card/95 backdrop-blur-sm shrink-0 game-border overflow-x-auto scrollbar-thin" style={{ height: 28 }}>
        <div className="flex items-center h-full min-w-max px-1.5 gap-0">
          {/* Status dot */}
          <div className="flex items-center gap-1 pr-2 border-r border-border/50 mr-1">
            <div className={`w-1.5 h-1.5 rounded-full ${
              simState === 'running' ? 'bg-positive game-pulse' :
              simState === 'paused' ? 'bg-warning' : 'bg-muted-foreground/40'
            }`} />
            {isActive && (
              <span className="text-[8px] font-mono text-muted-foreground/60">t{metrics.tick}</span>
            )}
          </div>

          {/* Compact metric cells */}
          {METRIC_CONFIGS.map((cfg) => {
            const value = metrics[cfg.key] as number;
            const status = getMetricStatus(value, cfg.thresholds, isActive);
            const MetricIcon = cfg.icon;
            const valueColor =
              status === 'crit' ? 'text-danger' :
              status === 'warn' ? 'text-warning' :
              status === 'healthy' ? 'text-positive' : 'text-muted-foreground/50';

            return (
              <div
                key={`ls-metric-${cfg.key}`}
                className="flex items-center gap-1 px-2 border-r border-border/30 h-full"
              >
                <MetricIcon size={8} className={valueColor} />
                <span className={`text-[9px] font-bold font-mono leading-none ${valueColor} ${status === 'crit' || status === 'healthy' ? 'text-game-glow' : ''}`}>
                  {isActive ? cfg.format(value) : '—'}
                </span>
                <span className="text-[7px] text-muted-foreground/40">{cfg.unit}</span>
              </div>
            );
          })}

          {/* Queue depth */}
          {metrics.queueDepth > 0 && isActive && (
            <div className="flex items-center gap-1 px-2">
              <span className="text-[7px] text-muted-foreground/50">Q</span>
              <span className={`text-[9px] font-bold font-mono ${metrics.queueDepth > 500 ? 'text-warning' : 'text-info'}`}>
                {metrics.queueDepth > 999 ? `${(metrics.queueDepth / 1000).toFixed(1)}k` : metrics.queueDepth}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border bg-card/95 backdrop-blur-sm shrink-0 overflow-x-auto scrollbar-thin">
      <div className="flex items-stretch min-w-max h-12 sm:h-14">
        {/* Status indicator */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 border-r border-border shrink-0">
          <div className={`w-2 h-2 rounded-full ${
            simState === 'running' ? 'bg-positive animate-pulse' :
            simState === 'paused' ? 'bg-warning' : 'bg-muted-foreground'
          }`} />
          <span className="text-xs font-medium text-muted-foreground capitalize font-mono-nums hidden sm:inline">{simState}</span>
          {isActive && (
            <span className="text-xs text-muted-foreground/60 font-mono-nums">t{metrics.tick}</span>
          )}
        </div>

        {/* Metrics */}
        {METRIC_CONFIGS.map((cfg) => {
          const value = metrics[cfg.key] as number;
          const status = getMetricStatus(value, cfg.thresholds, isActive);
          const MetricIcon = cfg.icon;

          const valueColor =
            status === 'crit' ? 'text-danger' :
            status === 'warn' ? 'text-warning' :
            status === 'healthy'? 'text-positive' : 'text-muted-foreground';

          const bgColor =
            status === 'crit' ? 'bg-danger/5' :
            status === 'warn' ? 'bg-warning/5' : '';

          return (
            <div
              key={`metric-${cfg.key}`}
              className={`flex flex-col justify-center px-2 sm:px-4 border-r border-border/50 ${bgColor} transition-colors duration-300 min-w-[56px] sm:min-w-[80px]`}
            >
              <div className="flex items-center gap-1 mb-0.5">
                <MetricIcon size={9} className={status === 'idle' ? 'text-muted-foreground/40' : valueColor} />
                <span className="text-muted-foreground/70 leading-none" style={{ fontSize: '9px' }}>{cfg.label}</span>
                {status === 'crit' && <AlertTriangle size={8} className="text-danger" />}
              </div>
              <div className="flex items-baseline gap-0.5">
                <span className={`metric-value text-sm sm:text-base font-bold leading-none ${valueColor}`}>
                  {isActive ? cfg.format(value) : '—'}
                </span>
                <span className="text-muted-foreground/50 leading-none" style={{ fontSize: '9px' }}>{cfg.unit}</span>
              </div>
            </div>
          );
        })}

        {/* Queue depth */}
        {metrics.queueDepth > 0 && isActive && (
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 border-l border-border shrink-0">
            <span className="text-xs text-muted-foreground hidden sm:inline">Queue</span>
            <span className={`metric-value text-sm font-bold ${metrics.queueDepth > 500 ? 'text-warning' : 'text-info'}`}>
              {metrics.queueDepth.toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}